import moment from "moment";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../constants/ZoomAttendance.constants";

/**
 * Formats start and end times into a human-readable session date string.
 * @param {string|Date} startTime - Session start time.
 * @param {string|Date} endTime - Session end time.
 * @returns {string} Formatted session date string (e.g. "12 Oct, 10:00–11:00 AM").
 */
export const formatSessionDate = (startTime, endTime) => {
  if (!startTime) return ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);

  const dateStr = startMoment.format("D MMM");
  const startTimeStr = startMoment.format("h:mm");
  const endTimeStr = endMoment.format("h:mm A");

  return `${dateStr}, ${startTimeStr}–${endTimeStr}`;
};

/**
 * Formats a raw percentage value to a rounded percentage string.
 * @param {number|string} percentage - Attendance percentage.
 * @returns {string} Formatted percentage (e.g. "85%").
 */
export const formatAttendancePercentage = (percentage) => {
  const numericVal = parseFloat(percentage);
  if (isNaN(numericVal)) return `0%`;
  return `${Math.round(numericVal)}%`;
};

/**
 * Formats a unique row identifier for a participant.
 * @param {Object} p - Participant data object.
 * @param {number} idx - Index fallback.
 * @returns {string} Formatted participant identifier.
 */
export const formatParticipantId = (p, idx) => {
  return (
    p?.exly_user_uuid ||
    p?.customer_email ||
    p?.customer_phone ||
    `${ZOOM_ATTENDANCE_CONSTANTS.LABELS.PARTICIPANT_PREFIX}${idx}_${
      p?.customer_name || ""
    }`
  );
};

/**
 * Formats total minutes attended to a display string.
 * @param {number} mins - Minutes attended.
 * @returns {string} Formatted string (e.g. "45 mins").
 */
export const formatMinutesText = (mins) => {
  if (mins !== undefined && mins !== null) {
    const num = Number(mins);
    if (isNaN(num) || num <= 0) {
      return "0 mins";
    }
    if (num > 0 && num < 1) {
      const secs = Math.round(num * 60);
      return secs > 0 ? `${secs} sec${secs === 1 ? "" : "s"}` : "< 1 min";
    }
    const roundedMins = Math.round(num);
    return `${roundedMins} min${roundedMins === 1 ? "" : "s"}`;
  }
  return "0 mins";
};

/**
 * Retrieves the display label corresponding to an attendance filter type.
 * @param {number} typeValue - Filter type constant value.
 * @returns {string} Display label string.
 */
export const getAttendanceCategoryLabel = (typeValue) => {
  const categoryLabels = {
    [ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.NO_FILTER]:
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.ALL_TAB,
    [ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.FULL_ATTENDANCE]:
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.FULL_ATTENDANCE_TAB,
    [ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.NO_ATTENDANCE]:
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_ATTENDANCE_TAB,
    [ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.PARTIAL_ATTENDANCE]:
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.PARTIAL_ATTENDANCE_TAB,
    [ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.UNRECOGNIZED]:
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.UNRECOGNIZED_ATTENDEES_TAB,
  };
  return categoryLabels[typeValue] || typeValue;
};

/**
 * Determines whether a participant is unrecognized.
 * @param {Object} p - Participant object.
 * @returns {boolean} True if unrecognized, false otherwise.
 */
export const isParticipantUnrecognized = (p) => {
  if (!p) return false;
  return (
    !p.exly_user_uuid ||
    p.attendance_type ===
      ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.UNRECOGNIZED
  );
};
